import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';

type GradeProgressHeaderProps = {
  gradedCount: number;
  totalCount: number;
  ungradedCount: number;
  draftCount: number;
  readyCount: number;
  className?: string;
  compact?: boolean;
};

export function GradeProgressHeader({
  gradedCount,
  totalCount,
  ungradedCount,
  draftCount,
  readyCount,
  className,
  compact = false,
}: GradeProgressHeaderProps): ReactElement {
  const { t } = useTranslation();
  const pct = totalCount > 0 ? Math.min(100, Math.round((gradedCount / totalCount) * 100)) : 0;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div>
        <p className="text-dg-label font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {t('audit.gradeStudio.progressLabel')}
        </p>
        <p className="mt-1 text-dg-body font-semibold text-slate-900 dark:text-white">
          {t('audit.gradeStudio.progressGraded', { graded: gradedCount, total: totalCount })}
        </p>
        <div
          className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200/90 dark:bg-slate-700/80"
          role="progressbar"
          aria-valuenow={gradedCount}
          aria-valuemin={0}
          aria-valuemax={totalCount}
        >
          <div
            className="h-full rounded-full bg-dg-accent-grade transition-[width] duration-300 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      {!compact ? (
        <div className="flex flex-wrap gap-2 text-dg-secondary">
          <span className="rounded-dg-control border border-slate-200/80 bg-white/80 px-2 py-1 font-medium dark:border-slate-600 dark:bg-slate-900/40">
            {t('audit.gradeStudio.statUngraded')}: {ungradedCount}
          </span>
          <span className="rounded-dg-control border border-dg-accent-grade/20 bg-dg-accent-gradeLight/60 px-2 py-1 font-medium dark:border-amber-800/30 dark:bg-amber-950/20">
            {t('audit.gradeStudio.statDrafts')}: {draftCount}
          </span>
          <span className="rounded-dg-control border border-emerald-500/20 bg-emerald-50/80 px-2 py-1 font-medium text-emerald-900 dark:border-emerald-800/30 dark:bg-emerald-950/25 dark:text-emerald-200">
            {t('audit.gradeStudio.statReady')}: {readyCount}
          </span>
        </div>
      ) : null}
    </div>
  );
}
